import styled, { keyframes } from "styled-components";

import { DATA_E2E_KEY, IStyledAttributes } from "@ryde/shared";
import { blueGreyMui, greyMui, purpleMui, rydeColors } from "@ryde/theme";

const highlightFlash = keyframes`
  0% {
    background: ${purpleMui._50};
    border-color: ${purpleMui._300};
    box-shadow: 0 0 0 2px ${purpleMui._200};
  }
  100% {
    background: ${rydeColors.white};
    border-color: ${greyMui._200};
    box-shadow: none;
  }
`;

export const CustomTranslationsStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "custom-translations-template-wrapper",
})`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;

  .ct-header {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .ct-header__title-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .ct-header__title {
    font-size: 16px;
    font-weight: 600;
    color: ${rydeColors.black};
    margin: 0;
  }

  .ct-header__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 22px;
    padding: 0 7px;
    border-radius: 11px;
    background: ${purpleMui._50};
    color: ${purpleMui._700};
    font-size: 12px;
    font-weight: 600;
  }

  .ct-header__hint {
    font-size: 13px;
    color: ${blueGreyMui._400};
    margin: 0;
  }

  .ct-picker {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 16px;
    border: 1px solid ${greyMui._200};
    border-radius: 10px;
    background: ${greyMui._50};
  }

  .ct-picker__label {
    font-size: 13px;
    font-weight: 600;
    color: ${blueGreyMui._500};
  }

  .ct-toolbar {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .ct-toolbar__search {
    max-width: 320px;
    width: 100%;
  }

  .ct-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .ct-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 32px 16px;
    border: 1px dashed ${greyMui._300};
    border-radius: 10px;
    text-align: center;
    color: ${blueGreyMui._400};
  }

  .ct-empty__title {
    font-size: 14px;
    font-weight: 600;
    color: ${blueGreyMui._500};
  }

  .ct-empty__text {
    font-size: 13px;
    margin: 0;
  }
`;

export const CustomTranslationRowStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "custom-translation-row",
})`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid ${greyMui._200};
  border-radius: 10px;
  background: ${rydeColors.white};
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    border-color: ${purpleMui._200};
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
  }

  &.ct-row--highlight {
    animation: ${highlightFlash} 2.5s ease-out;
  }

  @media (prefers-reduced-motion: reduce) {
    &.ct-row--highlight {
      animation: none;
    }
  }

  .ct-row__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
  }

  .ct-row__path-value {
    font-family: "Roboto Mono", "SFMono-Regular", Menlo, Consolas, monospace;
    font-size: 12.5px;
    font-weight: 600;
    color: ${purpleMui._700};
    word-break: break-all;
    padding-top: 4px;
  }

  .ct-row__fields {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px 16px;

    @media (max-width: 700px) {
      grid-template-columns: 1fr;
    }
  }

  .ct-row__field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  .ct-row__original {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    font-size: 11.5px;
    color: ${blueGreyMui._400};
    line-height: 1.5;
    word-break: break-word;
  }

  .ct-row__original-lang {
    font-weight: 600;
    color: ${blueGreyMui._500};
  }
`;

export const CustomTranslationOptionStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "custom-translation-option",
  },
)`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  min-width: 0;

  .ct-option__path {
    font-family: "Roboto Mono", "SFMono-Regular", Menlo, Consolas, monospace;
    font-size: 12.5px;
    font-weight: 600;
    color: ${purpleMui._700};
    word-break: break-all;
  }

  .ct-option__values {
    font-size: 11.5px;
    color: ${blueGreyMui._400};
  }

  .ct-option__lang {
    font-weight: 600;
    color: ${blueGreyMui._500};
  }
`;

export const CustomTranslationNoOptionsStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "custom-translation-no-options",
  },
)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  .ct-no-options__text {
    font-size: 13px;
    color: ${blueGreyMui._400};
  }

  .ct-no-options__note {
    font-size: 12px;
    color: ${blueGreyMui._400};
    font-style: italic;
  }

  .ct-no-options__add.MuiButton-root {
    text-transform: none;
    font-weight: 600;
    color: ${purpleMui._700};
    word-break: break-all;
    text-align: start;
    padding-inline: 4px;
  }
`;
